<template>
  <div>
    <next-button
      v-if="!esignResponseError"
      class="btn-primary"
      @click="esignClicked"
      text="Click Here to Sign"
    />
    <error v-if="esignResponseError" />
  </div>
</template>
<script setup lang="ts">
import { ref, defineEmits, computed, onBeforeMount } from "vue";
import { useStore } from "vuex";
import { $api } from "@/services/api";
import NextButton from "@/components/shared/NextButton.vue";
import Error from "@/components/shared/Error.vue";
import { ProductIds } from "@/config/constants";

const store = useStore();

const emit = defineEmits(["esignClicked"]);

const esignResponseError = ref<boolean>(false);

const productTypeId = computed(() => store.state.appData?.application?.productTypeId);
const legalPacketDocTypeId = computed(
  () =>
    store.state.lookupsModule?.lookups?.documentTypes?.find(
      (dt: any) => dt?.name?.trim() === "Legal Packet",
    )?.id || 0,
);

const showConfirmYourRefinanceDetails = computed(
  () => store.getters["consentsModule/SLR_PAYOFF_CONFIRMATION_EXISTS"] === false,
);

const showConfirmYourLoanDetails = computed(
  () => store.getters["consentsModule/CONFIRMATION_OF_LOAN_TERMS_CONSENT_EXISTS"] === false,
);

async function esignClicked(event: any) {
  if (productTypeId.value === ProductIds.REFI) {
    emit("esignClicked", event.userId);
    if (showConfirmYourRefinanceDetails.value) {
      store.dispatch("applicationInfoModule/openModal", "ConfirmYourRefinanceDetails");
    } else {
      // Open window synchronously before await — required for iOS Safari popup policy
      const newWindow = window.open("", "_blank");
      const esignResponse = await $api.applications.generateSigningUrl(
        store.state.appData.application.id,
        legalPacketDocTypeId.value,
      );
      if (esignResponse.signingUrl) {
        store.dispatch("applicationInfoModule/setCanEdit", false);
        if (newWindow) newWindow.location.href = esignResponse.signingUrl;
        await store.dispatch("checklistModule/getSortedChecklistItems");
        await store.dispatch("consentsModule/getConsentsForAllApplicants");
      } else {
        newWindow?.close();
        esignResponseError.value = true;
        await store.dispatch("consentsModule/getConsentsForAllApplicants");
      }
    }
  } else if (productTypeId.value === ProductIds.PEL || productTypeId.value === ProductIds.LOC) {
    emit("esignClicked", event.userId);
    if (showConfirmYourLoanDetails.value) {
      store.dispatch("applicationInfoModule/openModal", "ConfirmYourLoanDetails");
    } else {
      // Open window synchronously before await — required for iOS Safari popup policy
      const newWindow = window.open("", "_blank");
      const esignResponse = await $api.applications.generateSigningUrl(
        store.state.appData.application.id,
        legalPacketDocTypeId.value,
      );
      if (esignResponse.signingUrl) {
        store.dispatch("loanConfigurationModule/setEditable", false);
        if (newWindow) newWindow.location.href = esignResponse.signingUrl;
        await store.dispatch("checklistModule/getSortedChecklistItems");
        await store.dispatch("consentsModule/getConsentsForAllApplicants");
        await store.dispatch(
          "loanConfigurationModule/getPelDetails",
          store.state.appData?.application?.id,
        );
      } else {
        newWindow?.close();
        esignResponseError.value = true;
        await store.dispatch("consentsModule/getConsentsForAllApplicants");
      }
    }
  } else {
    return;
  }
}

onBeforeMount(async () => {
  if (
    productTypeId.value === ProductIds.REFI ||
    productTypeId.value === ProductIds.PEL ||
    productTypeId.value === ProductIds.LOC
  ) {
    await store.dispatch("consentsModule/getConsentsForAllApplicants");
  }
});
</script>
